import React, { useState } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Stack,
    Card,
    CardContent,
    Chip,
    IconButton,
    Collapse,
    Divider,
    Grid
} from '@mui/material';
import {
    History as LogIcon,
    Security as SecurityIcon,
    PersonAdd as PersonAddIcon,
    Refresh as RefreshIcon,
    Computer as DeviceIcon,
    ExpandMore as ExpandMoreIcon,
    Language as IpIcon
} from '@mui/icons-material';

const ActivityLogs = ({ userLogs, loadingLogs, selectedUser }) => {
    const [expandedLogId, setExpandedLogId] = useState(null);

    // Format timestamp from milliseconds
    const formatLogTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = new Date(timestamp);
            return date.toLocaleString();
        } catch {
            return 'Invalid Date';
        }
    };

    // Get log type display information
    const getLogTypeInfo = (logKey) => {
        const typeMap = {
            'SignIn': { label: 'Sign In', color: 'success', icon: SecurityIcon },
            'SignOut': { label: 'Sign Out', color: 'default', icon: SecurityIcon },
            'Register': { label: 'Register', color: 'info', icon: PersonAddIcon },
            'ExchangeTokenBy.AuthorizationCode': { label: 'Token Exchange', color: 'primary', icon: SecurityIcon },
            'ExchangeTokenBy.RefreshToken': { label: 'Token Refresh', color: 'primary', icon: RefreshIcon },
            'Interaction.Create': { label: 'Interaction', color: 'info', icon: DeviceIcon },
            'Interaction.End': { label: 'Session End', color: 'default', icon: DeviceIcon }
        };

        return typeMap[logKey] || { label: logKey || 'Unknown', color: 'default', icon: LogIcon };
    };

    // Get result color
    const getResultColor = (result) => {
        switch (result?.toLowerCase()) {
            case 'success': return 'success';
            case 'error': 
            case 'failed': return 'error';
            case 'warning': return 'warning';
            default: return 'default';
        }
    };

    // Toggle log expansion
    const toggleLogExpansion = (logId) => {
        setExpandedLogId(expandedLogId === logId ? null : logId);
    };

    return (
        <Box sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
            {/* Header */}
            <Box sx={{p: 2, borderBottom: 1, borderColor: 'divider'}}>
                <Typography variant="h6" gutterBottom>
                    User Authentication Logs
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Showing recent authentication and interaction events for {selectedUser?.username}
                </Typography>
            </Box>

            {/* Logs Content */}
            <Box sx={{flex: 1, overflow: 'auto', p: 2}}>
                {loadingLogs ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <CircularProgress size={20} />
                            <Typography variant="body2" color="text.secondary">
                                Loading activity logs...
                            </Typography>
                        </Stack>
                    </Box>
                ) : userLogs.length === 0 ? (
                    <Box sx={{textAlign: 'center', py: 4}}>
                        <LogIcon sx={{fontSize: 48, color: 'text.secondary', mb: 2}} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Activity Logs Found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            This user has no recorded activity logs in the system.
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={2}>
                        {userLogs.map((log) => {
                            const logType = getLogTypeInfo(log.key);
                            const LogTypeIcon = logType.icon;
                            const isExpanded = expandedLogId === log.id;

                            return (
                                <Card key={log.id} variant="outlined">
                                    <CardContent sx={{pb: 2}}>
                                        {/* Log Header */}
                                        <Box 
                                            sx={{
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'center',
                                                mb: 0.5,
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => toggleLogExpansion(log.id)}
                                        >
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                <LogTypeIcon color="action" />
                                                <Chip 
                                                    label={logType.label} 
                                                    color={logType.color} 
                                                    size="small" 
                                                />
                                                {log.payload?.result && (
                                                    <Chip 
                                                        label={log.payload.result} 
                                                        color={getResultColor(log.payload.result)} 
                                                        size="small" 
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Box>
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatLogTimestamp(log.createdAt)}
                                                </Typography>
                                                <IconButton size="small">
                                                    <ExpandMoreIcon 
                                                        sx={{
                                                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                            transition: 'transform 0.2s'
                                                        }}
                                                    />
                                                </IconButton>
                                            </Box>
                                        </Box>

                                        {/* Log Summary */}
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 0}}>
                                            {log.payload?.ip && (
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                    <IpIcon sx={{fontSize: 16, color: 'text.secondary'}} />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {log.payload.ip}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {log.payload?.userAgent && (
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                    <DeviceIcon sx={{fontSize: 16, color: 'text.secondary'}} />
                                                    <Typography variant="caption" color="text.secondary" noWrap>
                                                        {log.payload.userAgent.split(' ')[0] || 'Unknown Device'}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Expandable Details */}
                                        <Collapse in={isExpanded}>
                                            <Divider sx={{my: 2}} />
                                            <Grid container spacing={2}>
                                                <Grid size={{xs: 12, md: 6}}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Log Information
                                                    </Typography>
                                                    <Stack spacing={1}>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Log ID
                                                            </Typography>
                                                            <Typography variant="body2" sx={{fontFamily: 'monospace'}}>
                                                                {log.id}
                                                            </Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Tenant ID
                                                            </Typography>
                                                            <Typography variant="body2" sx={{fontFamily: 'monospace'}}>
                                                                {log.tenantId}
                                                            </Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Application ID
                                                            </Typography>
                                                            <Typography variant="body2" sx={{fontFamily: 'monospace'}}>
                                                                {log.payload?.applicationId || 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </Grid>

                                                <Grid size={{xs: 12, md: 6}}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Session Details
                                                    </Typography>
                                                    <Stack spacing={1}>
                                                        {log.payload?.sessionId && (
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Session ID
                                                                </Typography>
                                                                <Typography variant="body2" sx={{fontFamily: 'monospace'}}>
                                                                    {log.payload.sessionId}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {log.payload?.ip && (
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    IP Address
                                                                </Typography>
                                                                <Typography variant="body2" sx={{fontFamily: 'monospace'}}>
                                                                    {log.payload.ip}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {log.payload?.userAgent && (
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    User Agent
                                                                </Typography>
                                                                <Typography variant="body2" sx={{
                                                                    fontFamily: 'monospace',
                                                                    fontSize: '0.75rem',
                                                                    wordBreak: 'break-word'
                                                                }}>
                                                                    {log.payload.userAgent}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Stack>
                                                </Grid>

                                                {log.payload?.scope && (
                                                    <Grid size={{xs: 12}}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Scopes
                                                        </Typography>
                                                        <Box sx={{mt: 0.5}}>
                                                            {log.payload.scope.split(' ').map((scope, index) => (
                                                                <Chip 
                                                                    key={index} 
                                                                    label={scope} 
                                                                    size="small" 
                                                                    variant="outlined"
                                                                    sx={{mr: 0.5, mb: 0.5}}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Grid>
                                                )}

                                                {log.payload?.tokenTypes && (
                                                    <Grid size={{xs: 12}}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Token Types
                                                        </Typography>
                                                        <Box sx={{mt: 0.5}}>
                                                            {log.payload.tokenTypes.map((tokenType, index) => (
                                                                <Chip 
                                                                    key={index} 
                                                                    label={tokenType} 
                                                                    size="small" 
                                                                    color="info"
                                                                    variant="outlined"
                                                                    sx={{mr: 0.5, mb: 0.5}}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Collapse>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Stack>
                )}
            </Box>
        </Box>
    );
};

export default ActivityLogs;